import { useRef } from 'react';

import { motion, useScroll, useTransform } from 'framer-motion';

import useGsapHero from '../../hooks/useGsapHero';

import { BlurSplitText, ScrambleReveal, ClipRevealText } from '../TextEffects';



const CaseStudyHero = ({

  title,

  category,

  year,

  tagline,

  heroImage,

  heroLayout = 'contained',

  heroPlaceholder = false,

  stripLabel,

  index,

  studioLine,

}) => {

  const ref = useRef(null);

  const inView = useGsapHero(ref);

  const { scrollYProgress } = useScroll({

    target: ref,

    offset: ['start start', 'end start'],

  });



  const hintOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);



  const isFullscreen = heroLayout === 'fullscreen';

  const isOverlay = heroLayout === 'overlay';

  const isTextOnly = heroLayout === 'text';



  if (isTextOnly) {

    return (

      <header

        ref={ref}

        className="cs-hero is-text-only"

        data-testid="case-study-hero"

      >

        <div className="cs-hero-text">

          {stripLabel && index && (

            <span className="cs-hero-strip gsap-reveal">

              <span className="cs-hero-strip-num">({index})</span>

              <span className="cs-hero-strip-divider" aria-hidden="true" />

              <ScrambleReveal text={stripLabel} externalInView={inView} delay={0.12} />

            </span>

          )}

          <h1 className="cs-hero-title">

            <ClipRevealText text={title} as="span" externalInView={inView} delay={0.05} duration={0.85} />

          </h1>

          {studioLine && (

            <span className="cs-hero-studio gsap-reveal">

              <ScrambleReveal text={studioLine} externalInView={inView} delay={0.22} />

            </span>

          )}

          {tagline && <p className="cs-hero-tagline">{tagline}</p>}

        </div>

      </header>

    );

  }



  if (isOverlay) {

    return (

      <header

        ref={ref}

        className="cs-hero is-overlay"

        data-testid="case-study-hero"

      >

        <div className="cs-hero-overlay-media">

          {stripLabel && index && (

            <div className="cs-hero-overlay-strip gsap-reveal">

              <span className="cs-hero-strip-num">({index})</span>

              <span className="cs-hero-strip-divider" aria-hidden="true" />

              <ScrambleReveal text={stripLabel} externalInView={inView} delay={0.15} />

            </div>

          )}

          <div className="cs-hero-media-inner">

            {heroPlaceholder ? (

              <div className="cs-hero-shot-placeholder" aria-hidden="true" />

            ) : (

              <img src={heroImage} alt={`${title} — hero`} decoding="async" />

            )}

          </div>

          <div className="cs-hero-overlay-footer">

            <h1 className="cs-hero-overlay-title">

              <ClipRevealText

                text={title}

                as="span"

                externalInView={inView}

                delay={0.08}

                duration={1}

              />

            </h1>

            {studioLine && (

              <span className="cs-hero-overlay-studio gsap-reveal">

                <ScrambleReveal text={studioLine} externalInView={inView} delay={0.28} />

              </span>

            )}

          </div>

        </div>

        <motion.div

          className="cs-hero-scroll-hint cs-hero-scroll-hint--overlay"

          style={{ opacity: hintOpacity }}

          aria-hidden="true"

        >

          <motion.span

            className="cs-hero-scroll-line"

            animate={{ scaleY: [0, 1, 0] }}

            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}

            style={{ transformOrigin: 'top' }}

          />

          Scroll

        </motion.div>

      </header>

    );

  }



  return (

    <header

      ref={ref}

      className={`cs-hero${isFullscreen ? ' is-fullscreen' : ''}`}

      data-testid="case-study-hero"

    >

      <div className="cs-hero-text">

        {stripLabel && index && (

          <span className="cs-hero-strip gsap-reveal">

            <span className="cs-hero-strip-num">({index})</span>

            <span className="cs-hero-strip-divider" aria-hidden="true" />

            <ScrambleReveal text={stripLabel} externalInView={inView} delay={0.2} />

          </span>

        )}

        {!studioLine && (

          <span className="cs-hero-meta gsap-reveal">

            {category} — {year}

          </span>

        )}

        <h1 className="cs-hero-title">

          <BlurSplitText text={title} as="span" externalInView={inView} delay={0.15} stagger={0.06} />

        </h1>

        {studioLine && (

          <span className="cs-hero-studio gsap-reveal">

            <ScrambleReveal text={studioLine} externalInView={inView} delay={0.3} />

          </span>

        )}

        {tagline && <p className="cs-hero-tagline">{tagline}</p>}

      </div>



      <div className="cs-hero-media-wrap">

        <div className="cs-hero-media-inner">

          <img src={heroImage} alt={`${title} — hero`} decoding="async" />

        </div>

      </div>



      <motion.div className="cs-hero-scroll-hint" style={{ opacity: hintOpacity }} aria-hidden="true">

        <motion.span

          className="cs-hero-scroll-line"

          animate={{ scaleY: [0, 1, 0] }}

          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}

          style={{ transformOrigin: 'top' }}

        />

        Scroll to reveal

      </motion.div>

    </header>

  );

};



export default CaseStudyHero;

